// src/components/SearchModal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  Offcanvas,
  Button,
  Form,
  Row,
  Col
} from "react-bootstrap";
import { DayPicker } from "react-day-picker";
import { User, Search as SearchIcon, Search } from "lucide-react";
import "react-day-picker/dist/style.css";

const STORAGE_KEY = "bookingSearch";
const MOBILE_BREAKPOINT = 768;

export default function SearchModal({ show, onHide }) {
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState([]); // array of ages
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // disable past & today
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

  // hydrate saved state
  useEffect(() => {
    if (!show) return;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { from, to, adults: a, children: c } = JSON.parse(saved);
        setRange({
          from: from ? new Date(from) : undefined,
          to: to ? new Date(to) : undefined,
        });
        if (typeof a === "number" && a >= 1) setAdults(a);
        if (Array.isArray(c)) setChildren(c);
      } catch {}
    }
  }, [show]);

  // listen for resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // add/remove/update child
  const addChild = () => setChildren([...children, ""]);
  const removeChild = () => setChildren(children.slice(0, -1));
  const updateChildAge = (index, value) => {
    const updated = [...children];
    updated[index] = value;
    setChildren(updated);
  };

  // submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!range.from || !range.to) {
      alert("Please select a date range.");
      return;
    }
    const payload = {
      from: range.from.toISOString(),
      to: range.to.toISOString(),
      adults,
      children,
      guestCount: adults + children.length,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    onHide();
  };

  // ================= MOBILE CONTENT =================
  const mobileContent = (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Select Dates</Form.Label>
        <div className="d-flex justify-content-center">
          <DayPicker
            mode="range"
            selected={range}
            onSelect={setRange}
            disabled={{ before: tomorrow }}
            className="mx-auto"
          />
        </div>
      </Form.Group>

      {/* Adults */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Adults</Form.Label>
        <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
          <span>Adults</span>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => setAdults(Math.max(1, adults - 1))}>-</Button>
            <span>{adults}</span>
            <Button variant="outline-secondary" size="sm" onClick={() => setAdults(adults + 1)}>+</Button>
          </div>
        </div>
      </Form.Group>

      {/* Children */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Children</Form.Label>
        <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
          <span>Children</span>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" onClick={removeChild} disabled={children.length === 0}>-</Button>
            <span>{children.length}</span>
            <Button variant="outline-secondary" size="sm" onClick={addChild}>+</Button>
          </div>
        </div>
        <div style={{ maxHeight: 150, overflowY: "auto" }}>
          {children.map((age, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <span className="me-2">Child {index + 1} Age:</span>
              <Form.Control
                type="number"
                min={0}
                max={17}
                value={age}
                onChange={(e) => updateChildAge(index, e.target.value)}
                style={{ width:80 }}
                required
              />
            </div>
          ))}
        </div>
        <small className="text-muted d-block mt-1">
          Children under 7 stay free, 7–12 yrs: 50% of adult price, 12+ yrs: full adult price
        </small>
      </Form.Group>

      <Button type="submit" className="w-100 btn-own">
        <Search /> Search
      </Button>
    </Form>
  );

  // ================= DESKTOP CONTENT =================
  const desktopContent = (
    <Form onSubmit={handleSubmit}>
      <Row>
        {/* Left Column - Calendar */}
        <Col md={6} className="border-end pe-3">
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Select Dates</Form.Label>
            <div className="d-flex justify-content-center">
              <DayPicker
                mode="range"
                selected={range}
                onSelect={setRange}
                disabled={{ before: tomorrow }}
                className="mx-auto"
              />
            </div>
          </Form.Group>
        </Col>

        {/* Right Column - Guests */}
        <Col md={6} className="ps-3">
          {/* Adults */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Adults</Form.Label>
            <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
              <span>Adults</span>
              <div className="d-flex align-items-center gap-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setAdults(Math.max(1, adults - 1))}>-</Button>
                <span>{adults}</span>
                <Button variant="outline-secondary" size="sm" onClick={() => setAdults(adults + 1)}>+</Button>
              </div>
            </div>
          </Form.Group>

          {/* Children */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Children</Form.Label>
            <div className="d-flex align-items-center justify-content-between border rounded px-3 py-2 mb-2">
              <span>Children</span>
              <div className="d-flex align-items-center gap-2">
                <Button variant="outline-secondary" size="sm" onClick={removeChild} disabled={children.length === 0}>-</Button>
                <span>{children.length}</span>
                <Button variant="outline-secondary" size="sm" onClick={addChild}>+</Button>
              </div>
            </div>
            <div style={{ maxHeight: 150, overflowY: "auto" }}>
              {children.map((age, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <span className="me-2">Child {index + 1} Age:</span>
                  <Form.Control
                    type="number"
                    min={0}
                    max={17}
                    value={age}
                    onChange={(e) => updateChildAge(index, e.target.value)}
                    style={{ width: 80 }}
                  />
                </div>
              ))}
            </div>
            <small className="text-muted d-block mt-1">
              Children under 7 stay free, 7–12 yrs: 50% of adult price, 12+ yrs: full adult price
            </small>
          </Form.Group>

          <Button type="submit" className="w-100 btn-own">
            <Search /> Search
          </Button>
        </Col>
      </Row>
    </Form>
  );

  return isMobile ? (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="bottom"
      style={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, height: "auto" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <SearchIcon className="me-2" /> Search
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>{mobileContent}</Offcanvas.Body>
    </Offcanvas>
  ) : (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <SearchIcon className="me-2" /> Search
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{desktopContent}</Modal.Body>
    </Modal>
  );
}
